import { Container, Graphics, Point } from 'pixi.js';
import { LayerSystem } from '@rendering/LayerSystem';
import { WallSystem } from './WallSystem';

export type DoorOrientation = 'horizontal' | 'vertical';
export type DoorState = 'open' | 'closed';

export interface Door {
  id: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  floor: number;
  orientation: DoorOrientation;
  state: DoorState;
  sprite?: Graphics;
}

export class DoorSystem {
  private doors: Map<string, Door> = new Map();
  private doorSprites: Map<string, Graphics> = new Map();
  private readonly layerSystem: LayerSystem;
  private readonly container: Container;
  private readonly wallSystem: WallSystem;

  constructor(layerSystem: LayerSystem, container: Container, wallSystem: WallSystem) {
    this.layerSystem = layerSystem;
    this.container = container;
    this.wallSystem = wallSystem;
  }

  public addDoor(startX: number, startY: number, endX: number, endY: number, floor: number = 0): Door {
    const id = this.generateDoorId(startX, startY, endX, endY, floor);
    
    // Check if door already exists at this position
    if (this.doors.has(id)) {
      return this.doors.get(id)!;
    }

    const orientation: DoorOrientation = Math.abs(endX - startX) > Math.abs(endY - startY) ? 'horizontal' : 'vertical';
    
    const door: Door = {
      id,
      startX,
      startY,
      endX,
      endY,
      floor,
      orientation,
      state: 'open' // Default to open for now
    };

    this.doors.set(id, door);
    this.createDoorSprite(door);
    this.changeWallColorForDoor(door);
    
    return door;
  }

  public removeDoor(startX: number, startY: number, endX: number, endY: number, floor: number = 0): boolean {
    const id = this.generateDoorId(startX, startY, endX, endY, floor);
    const door = this.doors.get(id);
    
    if (!door) return false;

    this.destroyDoorSprite(id);
    this.doors.delete(id);
    
    return true;
  }

  public getDoorAt(x: number, y: number, floor: number = 0): Door | undefined {
    return Array.from(this.doors.values()).find(door => 
      door.floor === floor &&
      this.isPointOnDoor(x, y, door)
    );
  }

  public getAllDoors(): Door[] {
    return Array.from(this.doors.values());
  }

  public isDoorBlocking(startX: number, startY: number, endX: number, endY: number, floor: number = 0): boolean {
    const door = this.getDoorAt((startX + endX) / 2, (startY + endY) / 2, floor);
    return door ? door.state === 'closed' : false;
  }

  public toggleDoor(doorId: string): boolean {
    const door = this.doors.get(doorId);
    if (!door) return false;

    door.state = door.state === 'open' ? 'closed' : 'open';
    this.updateDoorSprite(door);
    
    return true;
  }

  public setDoorState(doorId: string, state: DoorState): boolean {
    const door = this.doors.get(doorId);
    if (!door) return false;

    door.state = state;
    this.updateDoorSprite(door);
    
    return true;
  }

  public clear(): void {
    // Remove all door sprites
    this.doorSprites.forEach((sprite, id) => {
      this.destroyDoorSprite(id);
    });
    
    this.doors.clear();
    this.doorSprites.clear();
  }

  private generateDoorId(startX: number, startY: number, endX: number, endY: number, floor: number): string {
    // Normalize coordinates to ensure consistent IDs regardless of start/end order
    const minX = Math.min(startX, endX);
    const maxX = Math.max(startX, endX);
    const minY = Math.min(startY, endY);
    const maxY = Math.max(startY, endY);
    
    return `door_${minX}_${minY}_${maxX}_${maxY}_${floor}`;
  }

  private isPointOnDoor(x: number, y: number, door: Door): boolean {
    const tolerance = 0.1;
    
    if (door.orientation === 'horizontal') {
      // Check if point is on the horizontal door line
      return Math.abs(y - door.startY) < tolerance && 
             x >= Math.min(door.startX, door.endX) - tolerance && 
             x <= Math.max(door.startX, door.endX) + tolerance;
    } else {
      // Check if point is on the vertical door line
      return Math.abs(x - door.startX) < tolerance && 
             y >= Math.min(door.startY, door.endY) - tolerance && 
             y <= Math.max(door.startY, door.endY) + tolerance;
    }
  }

  private createDoorSprite(door: Door): void {
    const sprite = new Graphics();
    this.updateDoorSprite(door);
    
    // Position the door sprite
    const centerX = (door.startX + door.endX) / 2;
    const centerY = (door.startY + door.endY) / 2;
    
    // Convert grid coordinates to world coordinates (simplified for now)
    const worldX = centerX * 64; // Assuming 64-pixel tile width
    const worldY = centerY * 32; // Assuming 32-pixel tile height
    
    sprite.position.set(worldX, worldY);
    
    this.doorSprites.set(door.id, sprite);
    this.layerSystem.add(sprite, 'Walls', new Point(centerX, centerY));
    
    door.sprite = sprite;
  }

  private updateDoorSprite(door: Door): void {
    const sprite = this.doorSprites.get(door.id);
    if (!sprite) return;

    sprite.clear();
    
    const width = Math.abs(door.endX - door.startX) * 64;
    const height = Math.abs(door.endY - door.startY) * 32;
    
    if (door.state === 'open') {
      // Open door - draw a subtle outline or opening indicator
      sprite.lineStyle(2, 0x8B4513, 0.6); // Brown outline
      if (door.orientation === 'horizontal') {
        sprite.moveTo(-width/2, 0);
        sprite.lineTo(width/2, 0);
      } else {
        sprite.moveTo(0, -height/2);
        sprite.lineTo(0, height/2);
      }
    } else {
      // Closed door - draw a solid door
      sprite.beginFill(0x8B4513, 0.8); // Brown door
      sprite.lineStyle(2, 0x654321, 1); // Darker brown border
      
      if (door.orientation === 'horizontal') {
        sprite.drawRect(-width/2, -4, width, 8);
      } else {
        sprite.drawRect(-4, -height/2, 8, height);
      }
      
      sprite.endFill();
    }
  }

  private destroyDoorSprite(doorId: string): void {
    const sprite = this.doorSprites.get(doorId);
    if (sprite) {
      sprite.parent?.removeChild(sprite);
      sprite.destroy();
      this.doorSprites.delete(doorId);
    }
  }

  private changeWallColorForDoor(door: Door): void {
    // Find the wall segment that matches this door's coordinates
    const wallSegment = this.wallSystem.getSegment(door.startX, door.startY, door.endX, door.endY, door.floor);
    
    if (wallSegment && wallSegment.graphic) {
      // Change the wall color to indicate a door is present
      // Cast to Graphics since walls should be Graphics objects
      const graphic = wallSegment.graphic as Graphics;
      if (graphic.tint !== undefined) {
        graphic.tint = 0x8B4513; // Brown color to indicate door
      }
      console.log(`Changed wall color for door at (${door.startX},${door.startY})→(${door.endX},${door.endY})`);
    } else {
      console.warn(`Could not find wall segment for door at (${door.startX},${door.startY})→(${door.endX},${door.endY})`);
    }
  }
}